import { Injectable } from '@angular/core';
import { AuthorizationService } from './authorization.service';

export type StudentViewMode = 'admin' | 'student';

/**
 * SessionStorage keys owned by `StudentViewModeService`. Exposed so that
 * session-lifecycle code (e.g. `AuthService.clearSession`) can wipe them on
 * logout without spreading magic strings across the codebase.
 */
export const STUDENT_VIEW_MODE_STORAGE_KEYS: readonly string[] = [
  'ui:studentViewMode',
] as const;

@Injectable({ providedIn: 'root' })
export class StudentViewModeService {
  private readonly storageKey = STUDENT_VIEW_MODE_STORAGE_KEYS[0];
  private mode: StudentViewMode = this.readStoredMode();

  constructor(private authorization: AuthorizationService) {}

  canToggle(): boolean {
    return this.authorization.getCurrentRole() !== 'STUDENT';
  }

  isStudentViewActive(): boolean {
    if (this.authorization.getCurrentRole() === 'STUDENT') {
      return true;
    }
    return this.mode === 'student';
  }

  isStudentModeSelected(): boolean {
    return this.mode === 'student';
  }

  setStudentMode(selected: boolean): void {
    if (!this.canToggle()) {
      return;
    }
    this.mode = selected ? 'student' : 'admin';
    this.persistMode();
  }

  private readStoredMode(): StudentViewMode {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(this.storageKey);
    } catch {
      raw = null;
    }
    return raw === 'student' ? 'student' : 'admin';
  }

  private persistMode(): void {
    try {
      sessionStorage.setItem(this.storageKey, this.mode);
    } catch {
      // Ignore storage errors in private/restricted browsing contexts.
    }
  }
}
